import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Typography } from '@mui/material'

import { GamesDataTableColumnType } from '../../types/MarketExplorerGamesDataTableColumns'
import { MarketExplorerSegmentConfiguration } from '../../types/MarketExplorerSegmentConfiguration'
import { SegmentQueryDataType } from '../MarketExplorerSegments/MarketExplorerSegment/MarketExplorerSegment'
import { MarketExplorerGamesDataTable, MarketExplorerGamesDataTableRow } from './MarketExplorerGamesDataTable/MarketExplorerGamesDataTable'

type MarketExplorerGamesDataProps = {
  segments: SegmentQueryDataType[]
  segmentsConfiguration?: MarketExplorerSegmentConfiguration[]
  isLoading?: boolean
  selectedColumns: GamesDataTableColumnType[]
  onSelectedColumnsChange: (columnIds: GamesDataTableColumnType[]) => void
  showDaysSinceRelease?: boolean
}

export const buildUniqueSegmentGameRows = (segments: SegmentQueryDataType[]): MarketExplorerGamesDataTableRow[] => {
  if (!segments) return []
  const uniqueSegment: MarketExplorerGamesDataTableRow[] = []
  const segmentKeyToIndex = new Map<string, number>()

  segments
    .flatMap((segment, index) => {
      return (
        segment?.data?.games.map((game) => ({
          game,
          segmentIndex: index,
          segmentIndexes: [index],
          segmentConfiguration: segment.segmentConfiguration,
          rowId: `${game.id}_${index}`,
          segmentData: segment,
          segments: [segment],
        })) || []
      )
    })
    .forEach((segmentGame) => {
      // Group rows by (game + market). The same game appearing in segments of different
      // markets must stay on separate rows; only same-game / same-market rows from different
      // segments should be merged into a single row.
      // Private own games all share appId 0, so they must be keyed by their unique game id
      // to avoid collapsing into a single row (GR-1365). Public games keep merging by appId
      // so the same app in same-market segments stays on one row (GR-746, GR-2351).
      const gameKey = segmentGame.game.appId > 0 ? segmentGame.game.appId : segmentGame.game.id
      const segmentKey = `${gameKey}-${segmentGame.segmentConfiguration.marketIso}`
      const existingIndex = segmentKeyToIndex.get(segmentKey)

      if (existingIndex === undefined) {
        segmentKeyToIndex.set(segmentKey, uniqueSegment.length)
        uniqueSegment.push(segmentGame)
      } else {
        uniqueSegment[existingIndex].segmentIndexes = uniqueSegment[existingIndex].segmentIndexes?.concat(segmentGame.segmentIndex)
        uniqueSegment[existingIndex].segments = uniqueSegment[existingIndex].segments?.concat(segmentGame.segmentData)
      }
    })
  return uniqueSegment
}

const MarketExplorerGamesData: React.FC<MarketExplorerGamesDataProps> = ({
  segments,
  segmentsConfiguration,
  isLoading,
  selectedColumns,
  onSelectedColumnsChange,
  showDaysSinceRelease,
}) => {
  const { t } = useTranslation()

  const segmentGames = useMemo(() => buildUniqueSegmentGameRows(segments), [segments])

  const tableConfig = useMemo(
    () => ({
      showSegment: true,
    }),
    []
  )

  return (
    <Box className="MarketExplorerGamesData">
      <MarketExplorerGamesDataTable
        rows={segmentGames}
        segmentsConfiguration={segmentsConfiguration}
        tableConfig={tableConfig}
        isLoading={isLoading}
        selectedColumns={selectedColumns}
        onSelectedColumnsChange={onSelectedColumnsChange}
        title={<Typography variant="h3">{t('market-explorer:games_list_tab')}</Typography>}
        showDaysSinceRelease={showDaysSinceRelease}
      />
    </Box>
  )
}

export default MarketExplorerGamesData
